import type { SortingState } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  MoreHorizontal,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { useState } from "react";
import { UsersPagination } from "@/components/admin/UsersPagination";
import { DropdownMenu } from "@/components/common";
import type { DropdownMenuItem } from "@/components/common/DropdownMenu";
import type { AgentCommission } from "@/types/commission";

interface CommissionsTableProps {
  data: AgentCommission[];
  search: string;
  onEdit: (agent: AgentCommission) => void;
  onResetAgent: (agent: AgentCommission) => void;
  canUpdate?: boolean;
}

const columnHelper = createColumnHelper<AgentCommission>();

export function CommissionsTable({
  data,
  search,
  onEdit,
  onResetAgent,
  canUpdate = true,
}: CommissionsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = [
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: "name",
      header: "Agent",
      cell: ({ row }) => (
        <div className="min-w-0">
          <p
            className="truncate text-sm font-medium text-main"
            title={`${row.original.firstName} ${row.original.lastName}`}
          >
            {row.original.firstName} {row.original.lastName}
          </p>
          <p className="truncate text-xs text-dim" title={row.original.email}>
            {row.original.email}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("commissionRate", {
      header: "Prowizja",
      cell: ({ row }) => {
        const { commissionRate, isCustom } = row.original;
        return (
          <span
            className={clsx(
              "text-sm font-medium",
              isCustom ? "text-adara-700 dark:text-adara-300" : "text-faint",
            )}
          >
            {commissionRate}%
          </span>
        );
      },
    }),
    ...(canUpdate
      ? [
          columnHelper.display({
            id: "actions",
            header: "",
            enableSorting: false,
            cell: ({ row }) => {
              const agent = row.original;
              const items: DropdownMenuItem[] = [
                {
                  label: "Ustaw prowizję",
                  icon: <Pencil size={14} />,
                  onClick: () => {
                    onEdit(agent);
                  },
                },
              ];

              if (agent.isCustom) {
                items.push({
                  label: "Resetuj do domyślnej",
                  icon: <RotateCcw size={14} />,
                  onClick: () => {
                    onResetAgent(agent);
                  },
                });
              }

              return (
                <DropdownMenu
                  trigger={<MoreHorizontal size={16} aria-hidden="true" />}
                  items={items}
                />
              );
            },
          }),
        ]
      : []),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const q = filterValue.toLowerCase();
      const agent = row.original;
      return (
        `${agent.firstName} ${agent.lastName}`.toLowerCase().includes(q) ||
        agent.email.toLowerCase().includes(q)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 10 },
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const totalCount = table.getFilteredRowModel().rows.length;
  const totalPages = table.getPageCount();

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-edge bg-subtle">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className={clsx(
                        "px-4 py-3 text-left text-xs font-medium text-dim",
                        canSort && "cursor-pointer select-none",
                      )}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {canSort && (
                          <span className="text-faint">
                            {sorted === "asc" ? (
                              <ArrowUp size={14} />
                            ) : sorted === "desc" ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUpDown size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-edge-soft">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="transition-colors hover:bg-subtle">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalCount > 10 && (
        <div className="px-4">
          <UsersPagination
            page={pageIndex + 1}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={(page) => {
              table.setPageIndex(page - 1);
            }}
            onPageSizeChange={(size) => {
              table.setPageSize(size);
            }}
          />
        </div>
      )}
    </div>
  );
}
